import moment from 'moment';
import { getTimeFromMinutes } from '../../../utils/convertTime';

const getFormatTimeFromMinutes = (minutes: number) => {
  const time = getTimeFromMinutes(minutes);
  time.push(0);

  return time
    .map(item => {
      return item.toString().length < 2 ? `0${item}` : item;
    })
    .join(':');
};

// Helper function for Russian pluralization
const getRussianPlural = (count: number, one: string, few: string, many: string): string => {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;

  if (n > 10 && n < 20) {
    return many;
  }
  if (n1 > 1 && n1 < 5) {
    return few;
  }
  if (n1 === 1) {
    return one;
  }
  return many;
};

// Format time string (HH:mm:ss.fff) as human-readable duration text
const getFormatDurationFromTimeString = (timeString: string): string => {
  if (!timeString || timeString === '00:00:00') {
    return '';
  }

  const parts = timeString.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (hours === 0) {
    const minuteWord = getRussianPlural(minutes, 'минута', 'минуты', 'минут');
    return `${minutes} ${minuteWord}`;
  } else if (minutes === 0) {
    const hourWord = getRussianPlural(hours, 'час', 'часа', 'часов');
    return `${hours} ${hourWord}`;
  } else {
    const hourWord = getRussianPlural(hours, 'час', 'часа', 'часов');
    const minuteWord = getRussianPlural(minutes, 'минута', 'минуты', 'минут');
    return `${hours} ${hourWord} ${minutes} ${minuteWord}`;
  }
};

// Format a duration string ("HH:mm:ss.fffffff") as "HH:mm", dropping sub-seconds.
// Empty/omitted → "". Present "00:00:00" → "00:00" (a real zero, distinct from omitted).
const formatDurationToHHmm = (timeString?: string): string => {
  if (!timeString) {
    return '';
  }
  const [hours = '00', minutes = '00'] = timeString.split(':');
  const pad = (value: string) => value.padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}`;
};

// Format an offset-aware ISO timestamp pair as a window "HH:mm – HH:mm".
// Missing sides render as "—". Both missing → "— – —".
// When the window crosses a calendar-day boundary (a night shift), each side also
// shows its date ("DD.MM HH:mm") so the day wrap is unambiguous instead of an
// ambiguous "22:00 – 06:00". The boundary is evaluated in the display tz.
const formatTimeWindow = (startIso?: string, endIso?: string): string => {
  const crossesMidnight = Boolean(startIso && endIso && !moment(startIso).isSame(endIso, 'day'));
  const format = (iso?: string) => (iso ? moment(iso).format(crossesMidnight ? 'DD.MM HH:mm' : 'HH:mm') : '—');
  return `${format(startIso)} – ${format(endIso)}`;
};

// Fact window: when the employee never entered a work zone, both fact times are
// omitted — show «нет входа в рабочую зону» instead of an empty window.
const formatFactWindow = (factStart?: string, factEnd?: string): string => {
  if (!factStart && !factEnd) {
    return 'нет входа в рабочую зону';
  }
  return formatTimeWindow(factStart, factEnd);
};

export {
  getFormatTimeFromMinutes,
  getFormatDurationFromTimeString,
  formatDurationToHHmm,
  formatTimeWindow,
  formatFactWindow
};
